// bd-deepcontext-zgbz — the jobs table shell. Column set is locked by the page
// design: Job / Source / Volume / Cost per day / Schedule / Actions.
// bd-deepcontext-ikwv — Status column dropped (option B): a running job's
// spinner + "Running now" lives in the Schedule cell instead, and inactive /
// expired rows are simply grayed out with no status pill (see JobRow.tsx).
import React from "react";
import JobRow from "./JobRow";
import type { LocalModelServer, ScheduledJob } from "./types";
import { tableFillHeightClass } from "../ui/tableFillBox";
import { Table, TableHead, TableHeaderCell, TableBody } from "../ui/table";

// bd-deepcontext-cply — Actions is content-width (no % share), not a
// proportional column: with table-auto, an unset/whitespace-nowrap column
// sizes to its widest cell (up to 4 action icons) instead of claiming extra
// width that left a dead band between Schedule and the icons. Job absorbs
// the width Actions no longer claims.
const COLUMNS: { label: string; className?: string }[] = [
  { label: "Job", className: "w-[30%]" },
  { label: "Source", className: "w-[12%]" },
  { label: "Volume", className: "w-[13%]" },
  { label: "Cost / day", className: "w-[10%]" },
  { label: "Schedule", className: "w-[14%]" },
  { label: "Actions", className: "whitespace-nowrap text-right" },
];

interface JobsTableProps {
  jobs: ScheduledJob[];
  isAdmin: boolean;
  /** deepcontext-63k8.3 — set for the allowed user: tag jobs with their server. */
  localModels?: LocalModelServer[];
  blueprintNames: Record<string, string>;
  // Table-fill contract (bd-n3uyh, deepcontext-wbra): on a full page this
  // stretches the <table> itself, not just its scroll box, so rows share
  // out the extra height instead of leaving a dead gap below the last row.
  isFullPage: boolean;
  onShowRuns: (job: ScheduledJob) => void;
  onEdit: (job: ScheduledJob) => void;
  onToggleActive: (job: ScheduledJob) => void;
  onExecute: (job: ScheduledJob) => void;
  onDelete: (job: ScheduledJob) => void;
  onDuplicate: (job: ScheduledJob) => void;
  onGoToBlueprint: (promptId: string) => void;
}

const JobsTable: React.FC<JobsTableProps> = ({ jobs, isFullPage, ...rowProps }) => (
  <Table className={`${tableFillHeightClass(isFullPage)} table-auto`} data-testid="jobs-table">
    <TableHead>
      <tr>
        {COLUMNS.map((column) => (
          <TableHeaderCell key={column.label} className={column.className}>
            {column.label}
          </TableHeaderCell>
        ))}
      </tr>
    </TableHead>
    <TableBody>
      {jobs.map((job) => (
        <JobRow key={job.id} job={job} {...rowProps} />
      ))}
    </TableBody>
  </Table>
);

export default JobsTable;
